/* eslint-disable no-param-reassign */
import { isEmpty } from 'lodash';
import {
  ALLOCATION_PERCENTAGE,
  BREAKPOINT_INFINITY,
  BREAKPOINT_PRO_RATA,
  BREAKPOINT_SERIE_ALIAS,
} from 'common/constants/cap-table/capTable';
import { largeCurrencyFormat } from 'common/formats/formats';
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { formatNumbers } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { BreakpointColumn } from '../../types/breakpoint-analysis.models';

export interface BreakpointReverseParserProps {
  cells: { [key: string]: ICell };
  columns: BreakpointColumn[];
  currencyCode: string;
  tableData: { currencySymbol: string; columns: BreakpointColumn[] };
}

const reverseParser = ({ cells, columns, currencyCode, tableData }: BreakpointReverseParserProps) => {
  if (isEmpty(columns)) {
    return [];
  }

  const { currencySymbol } = tableData;

  const parsedColumnsValues = Object.values(cells).filter(item => item.key);
  const prices = parsedColumnsValues.filter(item => item.name === 'price');

  const alphabet = alphabetGenerator([], columns.length + 1);

  const getTotalSeries = (col: BreakpointColumn) => col.series.reduce((acc, serie) => acc + Number(serie.value), 0);

  const getLowerLimit = (column: BreakpointColumn, columnIndex: number) => {
    if (columnIndex === 0) {
      return `${currencySymbol}0`;
    }
    if (column.series.every(serie => !serie.value)) {
      return `${currencySymbol}${alphabet[columnIndex]}`;
    }
    return formatNumbers({
      value: getTotalSeries(columns[columnIndex - 1]),
      format: { ...largeCurrencyFormat },
      currencyCode,
    } as any);
  };

  const getUpperLimit = (column: BreakpointColumn, columnIndex: number) => {
    if (columnIndex === columns.length - 1) {
      return BREAKPOINT_INFINITY;
    }
    if (column.series.every(serie => !serie.value)) {
      return `${currencySymbol}${alphabet[columnIndex + 1]}`;
    }
    return formatNumbers({
      value: getTotalSeries(column),
      format: { ...largeCurrencyFormat },
      currencyCode,
    } as any);
  };

  const getSeriesValue = (value: CellValue): CellValue => {
    if (value === BREAKPOINT_PRO_RATA) {
      return value;
    }
    if (!Number.isNaN(Number(value))) {
      return Number(value);
    }
    // Ignore non-numeric leaks (e.g. security names from rowConfig fallbacks)
    return undefined;
  };

  const getValueOrInfinity = (upper: number | string, value: number | string) => {
    if (upper === BREAKPOINT_INFINITY) {
      return upper;
    }

    return value;
  };

  const getSeriesCellsForColumn = (colIndex: number) => {
    const columnLetter = alphabet[colIndex];
    const keyPattern = new RegExp(`^${columnLetter}\\d+$`);
    return parsedColumnsValues
      .filter(item => {
        const isSeriesAlias = item.alias === BREAKPOINT_SERIE_ALIAS || item.alias === ALLOCATION_PERCENTAGE;
        return isSeriesAlias && typeof item.key === 'string' && keyPattern.test(item.key);
      })
      .sort((a, b) => {
        const rowA = Number(String(a.key).replace(columnLetter, ''));
        const rowB = Number(String(b.key).replace(columnLetter, ''));
        return rowA - rowB;
      });
  };

  columns.forEach((col: BreakpointColumn, colIndex: number) => {
    const bpSeries = getSeriesCellsForColumn(colIndex);
    col.series.forEach((serie: BreakpointColumn['series'][number], sIndex: number) => {
      const nextValue = getSeriesValue(bpSeries[sIndex]?.value);
      if (nextValue !== undefined) {
        serie.value = nextValue;
      }
    });

    const lowerLimit = getLowerLimit(col, colIndex);
    const upperLimit = getUpperLimit(col, colIndex);
    col.range = `${lowerLimit} to ${upperLimit}`;
    col.price = getValueOrInfinity(upperLimit, Number(prices[colIndex]?.value));
    col.total = getValueOrInfinity(upperLimit, getTotalSeries(col));
    tableData.columns[colIndex] = col;
  });
};

export default reverseParser;
